/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import * as OTel from '@ms/oteljs';

declare let Office: any;

const namespace = 'Office.ProgrammableSurfaces.SDX';
const ariaTenantToken = 'cf7a002ba9374401bace61695550f784-bc564f37-6fdf-43c3-a3d7-e50a9f6d40b4-7117';
const nexusTenantToken = 1783;

class OfficeJsSink implements OTel.TelemetrySink {
    public sendTelemetryEvent(event: OTel.TelemetryEvent): void {
        if (typeof Office !== 'undefined' && typeof Office.sendTelemetryEvent === 'function') {
            Office.sendTelemetryEvent(event);
        }
    }
}

let oTelLogger: OTel.TelemetryLogger | null = null;

function getLogger(): OTel.TelemetryLogger | null {
    if (!oTelLogger) {
        try {
            const logger = new OTel.TelemetryLogger();
            logger.setTenantToken(namespace, ariaTenantToken, nexusTenantToken);
            logger.addSink(new OfficeJsSink());
            oTelLogger = logger;
        } catch {
            return null;
        }
    }
    return oTelLogger;
}

export function logError(eventId: string, message: string): void {
    try {
        const logger = getLogger();
        if (!logger) return;
        logger.sendTelemetryEvent({
            eventName: namespace + '.Error',
            eventFlags: {
                diagnosticLevel: OTel.DiagnosticLevel.NecessaryServiceDataEvent,
                dataCategories: OTel.DataCategories.ProductServiceUsage | OTel.DataCategories.ProductServicePerformance
            },
            dataFields: [
                OTel.makeStringDataField('EventId', eventId),
                OTel.makeStringDataField('Message', message)
            ]
        });
    } catch { /* telemetry should never break the app */ }
}

export function logWarning(eventId: string, message: string): void {
    try {
        const logger = getLogger();
        if (!logger) return;
        logger.sendTelemetryEvent({
            eventName: namespace + '.Warning',
            eventFlags: {
                diagnosticLevel: OTel.DiagnosticLevel.FullEvent,
                dataCategories: OTel.DataCategories.ProductServiceUsage | OTel.DataCategories.ProductServicePerformance
            },
            dataFields: [
                OTel.makeStringDataField('EventId', eventId),
                OTel.makeStringDataField('Message', message)
            ]
        });
    } catch { /* telemetry should never break the app */ }
}

export function logUserAction(actionName: string, properties?: Record<string, string | number | boolean>): void {
    try {
        const logger = getLogger();
        if (!logger) return;

        const dataFields: OTel.DataField[] = [
            OTel.makeStringDataField('Action', actionName)
        ];

        if (properties) {
            for (const [key, value] of Object.entries(properties)) {
                if (typeof value === 'string') {
                    dataFields.push(OTel.makeStringDataField(key, value));
                } else if (typeof value === 'number') {
                    dataFields.push(OTel.makeDoubleDataField(key, value));
                } else if (typeof value === 'boolean') {
                    dataFields.push(OTel.makeBooleanDataField(key, value));
                }
            }
        }

        logger.sendTelemetryEvent({
            eventName: namespace + '.UserAction',
            eventFlags: {
                diagnosticLevel: OTel.DiagnosticLevel.FullEvent,
                dataCategories: OTel.DataCategories.ProductServiceUsage
            },
            dataFields
        });
    } catch { /* telemetry should never break the app */ }
}
